import React, { useState } from 'react';
import SEO from '../../components/common/SEO';
import Button from '../../components/common/Button';
import Card from '../../components/common/Card';
import { useSettings } from '../../hooks/useSettings';
import './GiftCard.css';

const giftCardOptions = [
  {
    id: 'gc-50',
    amount: 50,
    label: '$50',
    description: 'A thoughtful introduction to holistic healing'
  },
  {
    id: 'gc-100',
    amount: 100,
    label: '$100',
    description: 'Perfect for a tarot reading or healing session'
  },
  {
    id: 'gc-140',
    amount: 140,
    label: '$140',
    description: 'Covers a full Reiki Tune & Restore session'
  },
  {
    id: 'gc-180',
    amount: 180,
    label: '$180',
    description: 'Ideal for a Chakra Healing or Reiki Intro session'
  },
  {
    id: 'gc-240',
    amount: 240,
    label: '$240',
    description: 'The ultimate gift — a Deep Release & Rebalance session'
  },
  {
    id: 'gc-custom',
    amount: 'custom',
    label: 'Custom Amount',
    description: 'Choose your own amount'
  }
];

const GiftCard = () => {
  const { settings } = useSettings();
  const [selectedCard, setSelectedCard] = useState(null);
  const [customAmount, setCustomAmount] = useState('');
  const [formData, setFormData] = useState({
    senderName: '',
    senderEmail: '',
    recipientName: '',
    recipientEmail: '',
    message: ''
  });
  const [isSubmitted, setIsSubmitted] = useState(false);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    // For now, direct to contact for gift card purchase
    const amount = selectedCard?.amount === 'custom' ? customAmount : selectedCard?.amount;
    const subject = `Gift Card Purchase - $${amount}`;
    const body = `Hi Kirstie,\n\nI'd like to purchase a gift card.\n\nAmount: $${amount}\nFrom: ${formData.senderName}\nTo: ${formData.recipientName}\nMessage: ${formData.message || 'N/A'}\n\nPlease let me know how to proceed with payment.\n\nThank you!`;

    window.location.href = `mailto:${settings?.email || 'info@koholistichealth.com'}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
    setIsSubmitted(true);
  };

  return (
    <>
      <SEO
        title="Gift Cards | Give the Gift of Healing | KO Holistic Health Sydney"
        description="Give the gift of holistic healing with a KO Holistic Health gift card. Perfect for Reiki healing, tarot readings, soul coaching and more. Available in various amounts."
        url="/gift-card"
        keywords="holistic health gift card, reiki gift card sydney, healing gift voucher, wellness gift card, spiritual healing gift, tarot reading gift card"
        breadcrumbs={[{ name: 'Home', path: '/' }, { name: 'Gift Card', path: '/gift-card' }]}
      />

      <div className="gift-card-page">
        <section className="gift-card-hero">
          <div className="container">
            <h1>Gift Cards</h1>
            <p className="gift-card-hero__subtitle">
              Give the gift of healing, balance, and inner peace
            </p>
          </div>
        </section>

        <section className="section gift-card-content">
          <div className="container">
            {isSubmitted ? (
              <Card className="gift-card-success">
                <div className="gift-card-success__icon">
                  <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/>
                    <polyline points="22 4 12 14.01 9 11.01"/>
                  </svg>
                </div>
                <h2>Thank You!</h2>
                <p>Your email client should open with the gift card request details. If it didn't, please email us directly at <a href={`mailto:${settings?.email || 'info@koholistichealth.com'}`}>{settings?.email || 'info@koholistichealth.com'}</a>.</p>
                <Button onClick={() => setIsSubmitted(false)}>Purchase Another Gift Card</Button>
              </Card>
            ) : (
              <div className="gift-card-layout">
                <div className="gift-card-info">
                  <h2>The Perfect Gift for Someone Special</h2>
                  <p>
                    A KO Holistic Health gift card is a beautiful way to share the gift of healing with someone you care about.
                    Whether they're interested in Reiki healing, tarot readings, soul coaching, or any of our holistic services,
                    a gift card lets them choose the experience that's right for them.
                  </p>

                  <div className="gift-card-features">
                    <div className="gift-card-feature">
                      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/>
                      </svg>
                      <div>
                        <h4>Thoughtful & Personal</h4>
                        <p>Include a personal message with your gift</p>
                      </div>
                    </div>
                    <div className="gift-card-feature">
                      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <rect x="3" y="4" width="18" height="18" rx="2" ry="2"/>
                        <line x1="16" y1="2" x2="16" y2="6"/>
                        <line x1="8" y1="2" x2="8" y2="6"/>
                        <line x1="3" y1="10" x2="21" y2="10"/>
                      </svg>
                      <div>
                        <h4>Flexible Booking</h4>
                        <p>Valid for 12 months from purchase date</p>
                      </div>
                    </div>
                    <div className="gift-card-feature">
                      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <circle cx="12" cy="12" r="10"/>
                        <polyline points="12 6 12 12 16 14"/>
                      </svg>
                      <div>
                        <h4>Any Service</h4>
                        <p>Redeemable across all services and events</p>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="gift-card-form-section">
                  <Card className="gift-card-form-card">
                    <h3>Choose Your Gift Card</h3>

                    <div className="gift-card-options">
                      {giftCardOptions.map((option) => (
                        <button
                          key={option.id}
                          type="button"
                          className={`gift-card-option ${selectedCard?.id === option.id ? 'selected' : ''}`}
                          onClick={() => setSelectedCard(option)}
                        >
                          <span className="gift-card-option__amount">{option.label}</span>
                          <span className="gift-card-option__desc">{option.description}</span>
                        </button>
                      ))}
                    </div>

                    {selectedCard?.amount === 'custom' && (
                      <div className="form-group">
                        <label htmlFor="customAmount">Enter Amount ($AUD)</label>
                        <input
                          type="number"
                          id="customAmount"
                          min="25"
                          max="1500"
                          value={customAmount}
                          onChange={(e) => setCustomAmount(e.target.value)}
                          placeholder="Enter amount (min $25)"
                          required
                        />
                      </div>
                    )}

                    {selectedCard && (
                      <form onSubmit={handleSubmit} className="gift-card-form">
                        <h4>Your Details</h4>

                        <div className="form-group">
                          <label htmlFor="senderName">Your Name *</label>
                          <input
                            type="text"
                            id="senderName"
                            name="senderName"
                            value={formData.senderName}
                            onChange={handleChange}
                            required
                            placeholder="Your name"
                          />
                        </div>

                        <div className="form-group">
                          <label htmlFor="senderEmail">Your Email *</label>
                          <input
                            type="email"
                            id="senderEmail"
                            name="senderEmail"
                            value={formData.senderEmail}
                            onChange={handleChange}
                            required
                            placeholder="your.email@example.com"
                          />
                        </div>

                        <h4>Recipient Details</h4>

                        <div className="form-group">
                          <label htmlFor="recipientName">Recipient's Name *</label>
                          <input
                            type="text"
                            id="recipientName"
                            name="recipientName"
                            value={formData.recipientName}
                            onChange={handleChange}
                            required
                            placeholder="Their name"
                          />
                        </div>

                        <div className="form-group">
                          <label htmlFor="recipientEmail">Recipient's Email</label>
                          <input
                            type="email"
                            id="recipientEmail"
                            name="recipientEmail"
                            value={formData.recipientEmail}
                            onChange={handleChange}
                            placeholder="their.email@example.com (optional)"
                          />
                        </div>

                        <div className="form-group">
                          <label htmlFor="message">Personal Message</label>
                          <textarea
                            id="message"
                            name="message"
                            value={formData.message}
                            onChange={handleChange}
                            rows="3"
                            placeholder="Add a personal message for the recipient..."
                          ></textarea>
                        </div>

                        <Button type="submit" size="large" fullWidth>
                          Purchase Gift Card — ${selectedCard.amount === 'custom' ? (customAmount || '...') : selectedCard.amount}
                        </Button>

                        <p className="gift-card-note">
                          You'll be directed to email us with your gift card request. We'll process it and send you payment details within 24 hours.
                        </p>
                      </form>
                    )}
                  </Card>
                </div>
              </div>
            )}
          </div>
        </section>
      </div>
    </>
  );
};

export default GiftCard;
